/**
 * Pricing Utils — Frontend logic for Flusia pricing engine
 *
 * Used by UI components for tier display, price calculation,
 * and commission preview.
 */

// ── Types ──────────────────────────────────────────────────

export interface PricingTier {
  id: string;
  plan_id: string;
  min_users: number;
  max_users: number | null;
  price_monthly: number;
  is_per_user: boolean;
  is_custom: boolean;
  stripe_price_id_monthly: string | null;
  stripe_price_id_yearly: string | null;
  sort_order: number;
}

export interface Addon {
  id: string;
  name: string;
  description: string | null;
  addon_type: 'recurring' | 'one_time';
  price: number;
  addon_group: string | null;
  addon_group_exclusive: boolean;
  active: boolean;
  sort_order: number;
  metadata: Record<string, unknown>;
  unlocks_modules: string[];
}

export interface OrganizationAddon {
  id: string;
  organization_id: string;
  addon_id: string;
  status: 'active' | 'cancelled' | 'pending';
  stripe_subscription_item_id: string | null;
  activated_at: string;
  cancelled_at: string | null;
  expires_at: string | null;
  addon?: Addon;
}

export type SalesChannel = 'diretto' | 'pronto_pro' | 'ads';
export type PartnerTier = 'silver' | 'gold' | 'platinum';

// ── Constants ──────────────────────────────────────────────

export const ANNUAL_DISCOUNT = 0.20;

export const CHANNEL_COMMISSION_RATES: Record<SalesChannel, number> = {
  diretto: 20,
  pronto_pro: 16,
  ads: 12,
};

export const CHANNEL_LABELS: Record<SalesChannel, string> = {
  diretto: 'Diretto',
  pronto_pro: 'Pronto Pro',
  ads: 'ADS',
};

export const PARTNER_TIER_CONFIG: Record<PartnerTier, {
  min_clients: number;
  commission_rate: number;
  client_discount_rate: number;
  has_dedicated_account_manager: boolean;
  label: string;
}> = {
  silver: { min_clients: 1, commission_rate: 10, client_discount_rate: 0, has_dedicated_account_manager: false, label: 'Silver' },
  gold: { min_clients: 5, commission_rate: 15, client_discount_rate: 15, has_dedicated_account_manager: false, label: 'Gold' },
  platinum: { min_clients: 15, commission_rate: 20, client_discount_rate: 25, has_dedicated_account_manager: true, label: 'Platinum' },
};

export const PARTNER_SALES_OVERRIDE_RATE = 5;
export const PARTNER_ACTIVATION_BONUS = 50;
export const REFERRAL_DISCOUNT_RATE = 30;

// ── User Tier Ranges ───────────────────────────────────────

export const USER_TIER_RANGES = [
  { min: 1, max: 5, label: '1-5 utenti' },
  { min: 6, max: 15, label: '6-15 utenti' },
  { min: 16, max: 25, label: '16-25 utenti' },
  { min: 26, max: 50, label: '26-50 utenti' },
  { min: 51, max: null, label: '51+ utenti' },
] as const;

// ── Tier Lookup ────────────────────────────────────────────

export function getTierForUserCount(tiers: PricingTier[], userCount: number): PricingTier | null {
  if (!tiers.length || userCount < 1) return null;

  const sorted = [...tiers].sort((a, b) => a.min_users - b.min_users);

  for (const tier of sorted) {
    const maxUsers = tier.max_users ?? Infinity;
    if (userCount >= tier.min_users && userCount <= maxUsers) {
      return tier;
    }
  }

  return null;
}

/**
 * Restituisce il label della fascia utenti per un dato numero
 */
export function getUserTierLabel(userCount: number): string {
  for (const range of USER_TIER_RANGES) {
    const max = range.max ?? Infinity;
    if (userCount >= range.min && userCount <= max) {
      return range.label;
    }
  }
  return `${userCount} utenti`;
}

// ── Price Calculations ─────────────────────────────────────

export function calculateMonthlyPrice(
  tier: PricingTier,
  userCount: number,
  isAnnual: boolean
): number {
  if (tier.is_custom) return 0;

  let basePrice: number;
  if (tier.is_per_user) {
    basePrice = tier.price_monthly * userCount;
  } else {
    basePrice = tier.price_monthly;
  }

  if (isAnnual) {
    basePrice = basePrice * (1 - ANNUAL_DISCOUNT);
  }

  return Math.round(basePrice * 100) / 100;
}

export function calculateYearlyTotal(tier: PricingTier, userCount: number): number {
  const monthlyWithDiscount = calculateMonthlyPrice(tier, userCount, true);
  return Math.round(monthlyWithDiscount * 12 * 100) / 100;
}

export function calculateTotalWithAddons(
  baseMonthlyPrice: number,
  activeAddons: Addon[]
): number {
  const addonsTotal = activeAddons
    .filter(a => a.addon_type === 'recurring')
    .reduce((sum, a) => sum + a.price, 0);

  return Math.round((baseMonthlyPrice + addonsTotal) * 100) / 100;
}

/**
 * Calcola il risparmio annuale rispetto al mensile
 */
export function calculateAnnualSavings(tier: PricingTier, userCount: number): number {
  const monthlyTotal = calculateMonthlyPrice(tier, userCount, false) * 12;
  const yearlyTotal = calculateYearlyTotal(tier, userCount);
  return Math.round((monthlyTotal - yearlyTotal) * 100) / 100;
}

// ── Commission Calculations ────────────────────────────────

export function calculateSalesCommission(
  totalMonthly: number,
  channel: SalesChannel
): number {
  const rate = CHANNEL_COMMISSION_RATES[channel];
  return Math.round(totalMonthly * rate) / 100;
}

export function calculatePartnerCommission(
  totalMonthly: number,
  partnerTier: PartnerTier
): number {
  const config = PARTNER_TIER_CONFIG[partnerTier];
  return Math.round(totalMonthly * config.commission_rate) / 100;
}

export function calculatePartnerSalesOverride(totalMonthly: number): number {
  return Math.round(totalMonthly * PARTNER_SALES_OVERRIDE_RATE) / 100;
}

export function calculatePartnerTier(activeClientCount: number): PartnerTier {
  if (activeClientCount >= PARTNER_TIER_CONFIG.platinum.min_clients) return 'platinum';
  if (activeClientCount >= PARTNER_TIER_CONFIG.gold.min_clients) return 'gold';
  return 'silver';
}

// ── Formatting Helpers ─────────────────────────────────────

export function formatPrice(amount: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency,
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatPercentage(rate: number): string {
  return `${rate}%`;
}

// ── Product Category Discount ────────────────────────────

import type { ProductCategory } from '../types';

export interface EffectivePriceResult {
  regular_price: number;
  sale_price: number | null;
  source: 'individual' | 'category' | 'none';
  category_name?: string;
  category_discount_percentage?: number;
  has_conflict: boolean;
}

interface ProductPriceInput {
  regular_price?: number;
  sale_price?: number;
  categories?: ProductCategory[];
}

/**
 * Calcola il prezzo effettivo di un prodotto considerando:
 * 1. sale_price individuale (priorità)
 * 2. sconto % della prima categoria con discount_percentage > 0 (per sort_order)
 */
export function getEffectivePrice(product: ProductPriceInput): EffectivePriceResult {
  const regularPrice = product.regular_price || 0;
  const individualSalePrice = product.sale_price || null;

  const categoryWithDiscount = (product.categories || [])
    .filter(cat => cat.discount_percentage && cat.discount_percentage > 0)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))[0];

  const hasCategoryDiscount = !!categoryWithDiscount;
  const hasIndividualDiscount = individualSalePrice != null && individualSalePrice > 0;
  const hasConflict = hasIndividualDiscount && hasCategoryDiscount;

  if (hasIndividualDiscount) {
    return {
      regular_price: regularPrice,
      sale_price: individualSalePrice,
      source: 'individual',
      category_name: categoryWithDiscount?.name,
      category_discount_percentage: categoryWithDiscount?.discount_percentage,
      has_conflict: hasConflict,
    };
  }

  if (hasCategoryDiscount && regularPrice > 0) {
    const discountedPrice = Math.round(
      regularPrice * (1 - categoryWithDiscount.discount_percentage! / 100) * 100
    ) / 100;

    return {
      regular_price: regularPrice,
      sale_price: discountedPrice,
      source: 'category',
      category_name: categoryWithDiscount.name,
      category_discount_percentage: categoryWithDiscount.discount_percentage,
      has_conflict: false,
    };
  }

  return {
    regular_price: regularPrice,
    sale_price: null,
    source: 'none',
    has_conflict: false,
  };
}
